/**
 * /broadcasts/:broadcastId — live progress + recipient ledger.
 *
 * Polls every 2s while the broadcast is in a non-terminal state. Cycle
 * 12 ships polling; a future cycle can swap in a socket.io channel.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { api } from "../../../lib/api.js";
import { StatusPill } from "./index.js";

interface Broadcast {
  id: string;
  name: string;
  status: string;
  segmentId: string | null;
  igAccountId: string;
  body: { text: string };
  messageTag: string | null;
  scheduledAt: string | null;
  startedAt: string | null;
  finishedAt: string | null;
  eligibleCount: number | null;
  sentCount: number;
  skippedCount: number;
  failedCount: number;
  failureReason: string | null;
  createdAt: string;
}

interface RecipientRow {
  contactId: string;
  status: string;
  skipReason: string | null;
  sendAttempts: number;
  sentAt: string | null;
  igMessageId: string | null;
}

const NON_TERMINAL = new Set(["draft", "scheduled", "enumerating", "sending"]);

export const Route = createFileRoute("/_app/broadcasts/$broadcastId")({
  component: BroadcastDetailPage,
});

function BroadcastDetailPage() {
  const { broadcastId } = Route.useParams();
  const [b, setB] = useState<Broadcast | null>(null);
  const [recipients, setRecipients] = useState<RecipientRow[]>([]);
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const pollTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    void loadAll();
    return () => {
      if (pollTimer.current) clearTimeout(pollTimer.current);
    };
  }, [broadcastId]);

  useEffect(() => {
    void loadRecipients();
  }, [statusFilter]);

  // Schedule the next poll any time the broadcast moves through a
  // non-terminal status. Stops polling once we hit done/cancelled/failed.
  useEffect(() => {
    if (!b) return;
    if (!NON_TERMINAL.has(b.status)) return;
    if (pollTimer.current) clearTimeout(pollTimer.current);
    pollTimer.current = setTimeout(() => {
      void loadAll();
    }, 2000);
    return () => {
      if (pollTimer.current) clearTimeout(pollTimer.current);
    };
  }, [b?.status, b?.sentCount, b?.skippedCount, b?.failedCount]);

  async function loadAll() {
    try {
      const r = await api.get<{ broadcast: Broadcast }>(`/broadcasts/${broadcastId}`);
      setB(r.data.broadcast);
      await loadRecipients();
    } catch {
      setError("Failed to load broadcast");
    } finally {
      setLoading(false);
    }
  }

  async function loadRecipients() {
    try {
      const params: Record<string, string> = { limit: "100" };
      if (statusFilter) params.status = statusFilter;
      const r = await api.get<{ recipients: RecipientRow[] }>(
        `/broadcasts/${broadcastId}/recipients`,
        { params },
      );
      setRecipients(r.data.recipients);
    } catch {
      // Non-fatal; broadcast may still be enumerating
    }
  }

  async function cancel() {
    if (!b) return;
    if (!confirm("Cancel this broadcast? Pending recipients won't be sent.")) return;
    try {
      const r = await api.post<{ broadcast: Broadcast }>(`/broadcasts/${broadcastId}/cancel`, {});
      setB(r.data.broadcast);
    } catch {
      setError("Failed to cancel");
    }
  }

  const progress = useMemo(() => {
    if (!b || !b.eligibleCount) return 0;
    const processed = b.sentCount + b.skippedCount + b.failedCount;
    return Math.min(100, Math.round((processed / b.eligibleCount) * 100));
  }, [b]);

  if (loading) return <p>Loading…</p>;
  if (!b) return <p>Not found.</p>;

  const cancelable = b.status === "draft" || b.status === "scheduled" || b.status === "enumerating" || b.status === "sending";

  return (
    <section style={{ maxWidth: 1000, margin: "0 auto" }}>
      <header style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <h1 style={{ margin: 0, fontSize: 28 }}>{b.name}</h1>
        <div style={{ display: "flex", gap: 8 }}>
          <StatusPill status={b.status} />
          {cancelable ? (
            <button onClick={cancel} className="brut-button is-danger" style={{ fontSize: 13, padding: "6px 12px" }}>
              Cancel
            </button>
          ) : null}
        </div>
      </header>

      {error ? (
        <div className="brut-card" style={{ background: "#ffd7d7", padding: 10, marginBottom: 12 }}>{error}</div>
      ) : null}
      {b.failureReason ? (
        <div className="brut-card" style={{ background: "#ffd7d7", padding: 10, marginBottom: 12 }}>
          Error: {b.failureReason}
        </div>
      ) : null}

      <div className="brut-card" style={{ padding: 14, marginBottom: 16, display: "grid", gap: 12 }}>
        <div>
          <div style={{ fontSize: 13, color: "var(--muted)", marginBottom: 4 }}>Body</div>
          <pre style={{ margin: 0, fontFamily: "inherit", whiteSpace: "pre-wrap" }}>{b.body.text}</pre>
        </div>
        <div style={{ display: "flex", gap: 24, fontSize: 13 }}>
          <div>
            <div style={{ color: "var(--muted)" }}>Eligible</div>
            <div style={{ fontWeight: 800, fontSize: 22 }}>
              {b.eligibleCount?.toLocaleString() ?? "—"}
            </div>
          </div>
          <Counter label="Sent" value={b.sentCount} />
          <Counter label="Skipped" value={b.skippedCount} />
          <Counter label="Failed" value={b.failedCount} color={b.failedCount > 0 ? "#b40000" : undefined} />
        </div>
        {NON_TERMINAL.has(b.status) ? (
          <div>
            <div
              style={{
                width: "100%",
                height: 12,
                background: "#eee",
                border: "2px solid var(--ink)",
                position: "relative",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  width: `${progress}%`,
                  height: "100%",
                  background: "var(--accent)",
                  transition: "width 0.4s linear",
                }}
              />
            </div>
            <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 4 }}>
              {progress}% · auto-refreshing
            </div>
          </div>
        ) : null}
      </div>

      <div className="brut-card" style={{ padding: 14 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
          <strong>Recipient ledger</strong>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="brut-input"
            style={{ width: 160 }}
          >
            <option value="">All statuses</option>
            <option value="pending">Pending</option>
            <option value="sending">Sending</option>
            <option value="sent">Sent</option>
            <option value="skipped">Skipped</option>
            <option value="failed">Failed</option>
          </select>
        </div>
        {recipients.length === 0 ? (
          <p style={{ color: "var(--muted)" }}>No recipients yet.</p>
        ) : (
          <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ borderBottom: "2px solid var(--ink)" }}>
                <th style={{ textAlign: "left", padding: 6 }}>Contact</th>
                <th style={{ textAlign: "left", padding: 6 }}>Status</th>
                <th style={{ textAlign: "left", padding: 6 }}>Reason</th>
                <th style={{ textAlign: "left", padding: 6 }}>Sent at</th>
              </tr>
            </thead>
            <tbody>
              {recipients.map((r) => (
                <tr key={r.contactId} style={{ borderBottom: "1px solid #eee" }}>
                  <td style={{ padding: 6 }}>
                    <code style={{ fontSize: 12 }}>{r.contactId.slice(0, 8)}…</code>
                  </td>
                  <td style={{ padding: 6 }}><StatusPill status={r.status} /></td>
                  <td style={{ padding: 6, color: "var(--muted)" }}>
                    {r.skipReason ?? "—"}
                  </td>
                  <td style={{ padding: 6, color: "var(--muted)" }}>
                    {r.sentAt ? new Date(r.sentAt).toLocaleString() : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}

function Counter({ label, value, color }: { label: string; value: number; color?: string }) {
  return (
    <div>
      <div style={{ color: "var(--muted)" }}>{label}</div>
      <div style={{ fontWeight: 800, fontSize: 22, color }}>{value.toLocaleString()}</div>
    </div>
  );
}
